import React from 'react';

import { Center, Spinner, useToast } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';

import { createRequest, fetchPortal, uploadPortalLogo } from 'api/cms';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import NotFoundView from 'routes/portal/NotFound/NotFound.view';

import { SitePortalProps } from './SitePortal.props';

import SitePortalView from './SitePortal.view';

const SitePortalContainer = (props: SitePortalProps) => {
  const toast = useToast();
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const portalName = params.portalName || '';

  const queryKey = ['fetch-site-portal', portalName];
  const queryFn = () => fetchPortal(portalName);
  const options = {
    enabled: !!portalName,
  };

  const { data, isError, isLoading } = useQuery(queryKey, queryFn, options);

  const { mutate, isLoading: isCreating } = useMutation(
    (_payload: any) => createRequest(_payload, data?._id || ''),
    {
      onSuccess: (res) => {
        const portalId = data?._id || '';
        const orderId = res?.item?._id || '';

        toast({
          status: 'success',
          title: 'Success',
          description: 'Request successfully created!',
          duration: 5000,
          isClosable: true,
        });

        setTimeout(() => {
          navigate(`/cms/${portalId}/${orderId}`);
        }, 500);
      },
    },
  );

  const { mutate: uploadLogo, isLoading: isUploadingLogo } = useMutation(
    (file: File) => uploadPortalLogo(file, portalName),
    {
      onSuccess: () => {
        // Refetch so the portal picks up a presigned URL for the new key.
        queryClient.invalidateQueries(queryKey);

        toast({
          status: 'success',
          title: 'Logo updated',
          duration: 4000,
          isClosable: true,
        });
      },
      onError: (error: any) => {
        // The endpoint is rate limited per day, so a 429 is an expected
        // outcome worth naming rather than a generic failure.
        const isRateLimited = error?.response?.status === 429;

        toast({
          status: 'error',
          title: isRateLimited ? 'Too many uploads' : 'Upload failed',
          description: isRateLimited
            ? 'The logo has been changed too many times today. Please try again tomorrow.'
            : error?.response?.data?.message ||
              'The logo could not be uploaded.',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const onSubmit = async (values: any) => {
    const details = values?.details?.map?.((d: any) => ({
      ...d,
      casesCount: Number(d?.casesCount || '0'),
    }));

    const payload = {
      details: details.filter((d: any) => d?.casesCount > 0),
      notes: values?.notes,
    };

    await mutate(payload);
  };

  return (
    <>
      {isLoading ? (
        <Center flex={1}>
          <Spinner size="lg" />
        </Center>
      ) : isError || data === undefined ? (
        <Center flex={1}>
          <NotFoundView />
        </Center>
      ) : (
        <SitePortalView
          data={data}
          onSubmit={onSubmit}
          isCreating={isCreating}
          onLogoSelect={uploadLogo}
          isUploadingLogo={isUploadingLogo}
        />
      )}
    </>
  );
};

export default SitePortalContainer;
